// enableTreeDrawing.js
import * as THREE from "three";
import { TransformControls } from "three/examples/jsm/controls/TransformControls.js";
import { TREES, VARS } from "../Global/variables";

export function enableTreeDrawing(
  scene,
  camera,
  renderer,
  targetMeshes,
  setTrees,
  shape = "cone" // "cone" | "circle"
) {
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();
  let drawingMode = true;

  let previewTree = null;
  let startPoint = null;
  let selectedTree = null;
  let heightDrag = false;
  let draggingTree = false;
  let dragOffset = new THREE.Vector3();

  console.log("🌳 Tree drawing mode enabled.");

  // ✅ helper: notify UI (Designer) about selection changes
  function emitTreeSelected(treeOrNull) {
    if (typeof window === "undefined") return;

    VARS.selectedTree = treeOrNull || null;
    window.dispatchEvent(
      new CustomEvent("TREE_SELECTED", { detail: { tree: treeOrNull || null } })
    );
  }

  function attachControlsToTree(tree) {
    if (!tree) return;

    // ✅ if locked: ensure controls are not attached
    if (tree.userData?.locked) {
      if (window.transformControls?.object === tree) {
        window.transformControls.detach();
      }
      return;
    }

    if (window.transformControls) {
      window.transformControls.detach();
      if (scene.children.includes(window.transformControls)) {
        scene.remove(window.transformControls);
      }
      window.transformControls.dispose?.();
      window.transformControls = null;
    }

    const transformControls = new TransformControls(camera, renderer.domElement);
    transformControls.attach(tree);
    transformControls.setMode("translate");
    transformControls.showZ = false;

    transformControls.addEventListener("objectChange", () => {
      if (!transformControls.object) return;

      // ✅ block changes if locked mid-drag
      if (transformControls.object.userData?.locked) {
        transformControls.detach();
        return;
      }

      transformControls.object.position.z = tree.userData.groundZ;
      highlight(transformControls.object, true);
    });

    transformControls.addEventListener("dragging-changed", (e) => {
      const orbit = scene.userData.orbitControls;
      if (orbit) orbit.enabled = !e.value;

      if (!e.value && transformControls.object) {
        snapTreeToGround(transformControls.object);
      }
    });

    scene.add(transformControls);
    window.transformControls = transformControls;
  }

  window.attachControlsToTree = attachControlsToTree;

  function highlight(tree, on) {
    tree.traverse((o) => {
      if (o.material?.emissive) {
        o.material.emissive.set(on ? 0x444400 : 0x000000);
      }
    });
  }

  // ✅ always snap to world z=0
  function snapTreeToGround(tree) {
    tree.position.z = 0;
    tree.userData.groundZ = 0;
  }

  function createTreeMesh() {
    const tree = new THREE.Group();
    tree.name = `InteractiveTree_${Date.now()}`;

    const trunkHeight = 1.2;

    const trunk = new THREE.Mesh(
      new THREE.CylinderGeometry(0.05, 0.1, trunkHeight, 8),
      new THREE.MeshStandardMaterial({ color: 0x6b4f2a })
    );
    trunk.position.y = trunkHeight / 2;

    let foliage;
    if (shape === "circle") {
      foliage = new THREE.Mesh(
        new THREE.SphereGeometry(0.9, 12, 12),
        new THREE.MeshStandardMaterial({ color: 0x2f7d32 })
      );
      foliage.position.y = trunkHeight + 0.9;
    } else {
      foliage = new THREE.Mesh(
        new THREE.ConeGeometry(0.9, 1.8, 10),
        new THREE.MeshStandardMaterial({ color: 0x2f7d32 })
      );
      foliage.position.y = trunkHeight + 0.9;
    }

    const arrow = new THREE.Mesh(
      new THREE.ConeGeometry(0.12, 0.3, 6),
      new THREE.MeshStandardMaterial({ color: 0xff0000 })
    );
    arrow.position.y = trunkHeight + 2.1;
    arrow.name = "heightArrow";
    arrow.visible = false;

    tree.add(trunk);
    tree.add(foliage);
    tree.add(arrow);

    tree.userData.trunk = trunk;
    tree.userData.foliage = foliage;
    tree.userData.arrow = arrow;

    // ✅ lock flag default
    tree.userData.locked = false;

    // keep constants for UI editing too
    tree.userData.__trunkBaseHeight = trunkHeight;
    tree.userData.__foliageOffset = 0.9;
    tree.userData.__arrowOffset = 2.1;

    tree.rotation.x = Math.PI / 2;

    return tree;
  }

  function onHover(event) {
    if (draggingTree || heightDrag) return;

    const rect = renderer.domElement.getBoundingClientRect();
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(mouse, camera);

    const hits = raycaster.intersectObjects(TREES.trees, true);

    if (!hits.length) {
      renderer.domElement.style.cursor = "default";
      return;
    }

    const root = hits[0].object.parent;
    if (root?.userData?.locked) {
      renderer.domElement.style.cursor = "default";
    } else {
      renderer.domElement.style.cursor = "move";
    }
  }

  function onPointerDown(event) {
    const rect = renderer.domElement.getBoundingClientRect();
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(mouse, camera);

    // ✅ FIRST: check if user clicked an existing tree
    const hits = raycaster.intersectObjects(TREES.trees, true);

    if (hits.length) {
      const root = hits[0].object.parent;

      if (selectedTree) {
        highlight(selectedTree, false);
        if (selectedTree.userData.arrow) selectedTree.userData.arrow.visible = false;
      }

      selectedTree = root;
      highlight(selectedTree, true);

      const isLocked = !!selectedTree.userData?.locked;

      // ✅ show arrow only if unlocked
      if (selectedTree.userData.arrow) {
        selectedTree.userData.arrow.visible = !isLocked;
      }

      // ✅ notify UI
      emitTreeSelected(selectedTree);

      // ✅ if locked: allow selection but block moving/resizing
      if (isLocked) {
        // detach transform controls if they were attached
        if (window.transformControls?.object === selectedTree) {
          window.transformControls.detach();
        }
        return;
      }

      // clicking the arrow means height drag
      if (hits[0].object.name === "heightArrow") {
        heightDrag = true;

        if (window.transformControls) {
          window.transformControls.detach();
        }

        renderer.domElement.addEventListener("pointermove", onHeightDrag);
        renderer.domElement.addEventListener("pointerup", stopHeightDrag, {
          once: true,
        });
        return;
      }

      startTreeDrag(selectedTree, hits[0].point);
      return;
    }

    // ✅ if clicked anywhere else: unselect tree
    if (selectedTree) {
      highlight(selectedTree, false);
      if (selectedTree.userData.arrow) selectedTree.userData.arrow.visible = false;
      selectedTree = null;
      emitTreeSelected(null);
    }

    // if not drawing mode, stop here
    if (!drawingMode) return;
    drawingMode = false;

    const intersects = raycaster.intersectObjects(
      Array.isArray(targetMeshes) ? targetMeshes : [targetMeshes],
      true
    );
    if (!intersects.length) return;

    startPoint = intersects[0].point.clone();
    startPoint.z = 0;

    previewTree = createTreeMesh();
    previewTree.traverse((o) => {
      if (o.material) o.material.wireframe = true;
    });

    previewTree.position.set(startPoint.x, startPoint.y, 0);
    previewTree.userData.groundZ = 0;

    scene.add(previewTree);

    renderer.domElement.addEventListener("pointermove", onPointerMove);
    renderer.domElement.addEventListener("pointerup", onPointerUp, { once: true });
  }

  function onPointerMove(event) {
    if (!previewTree || !startPoint) return;

    const rect = renderer.domElement.getBoundingClientRect();
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(mouse, camera);

    const intersects = raycaster.intersectObjects(
      Array.isArray(targetMeshes) ? targetMeshes : [targetMeshes],
      true
    );
    if (!intersects.length) return;

    const p = intersects[0].point.clone();
    p.z = 0;

    const dist = startPoint.distanceTo(p);
    const scale = Math.max(0.3, dist);

    previewTree.scale.set(scale, scale, scale);
    previewTree.position.z = 0;
  }

  function onPointerUp() {
    if (!previewTree) return;

    previewTree.traverse((o) => {
      if (o.material) o.material.wireframe = false;
    });

    const tree = previewTree;
    previewTree = null;
    startPoint = null;

    tree.position.z = 0;
    tree.userData.groundZ = 0;

    TREES.trees.push(tree);
    setTrees((prev) => [...prev, tree]);
  }

  function onHeightDrag(event) {
    if (!selectedTree || !heightDrag) return;

    // ✅ block resize if locked
    if (selectedTree.userData?.locked) return;

    highlight(selectedTree, true);

    const delta = event.movementY * -0.01;
    const trunk = selectedTree.userData.trunk;
    const foliage = selectedTree.userData.foliage;
    const arrow = selectedTree.userData.arrow;

    const newH = Math.max(0.5, trunk.scale.y + delta);
    trunk.scale.y = newH;

    const base = selectedTree.userData.__trunkBaseHeight ?? 1.2;
    const folOff = selectedTree.userData.__foliageOffset ?? 0.9;
    const arrOff = selectedTree.userData.__arrowOffset ?? 2.1;

    trunk.position.y = (base * newH) / 2;
    foliage.position.y = base * newH + folOff;
    arrow.position.y = base * newH + arrOff;
  }

  function stopHeightDrag() {
    heightDrag = false;
    renderer.domElement.removeEventListener("pointermove", onHeightDrag);

    if (selectedTree) {
      highlight(selectedTree, true);

      const isLocked = !!selectedTree.userData?.locked;
      if (selectedTree.userData.arrow) selectedTree.userData.arrow.visible = !isLocked;
    }
  }

  function startTreeDrag(tree, hitPoint) {
    // ✅ block move if locked
    if (tree?.userData?.locked) return;

    draggingTree = true;
    selectedTree = tree;
    highlight(selectedTree, true);

    dragOffset.copy(hitPoint).sub(tree.position);

    renderer.domElement.addEventListener("pointermove", onTreeDragMove);
    renderer.domElement.addEventListener("pointerup", stopTreeDrag, { once: true });
  }

  function onTreeDragMove(event) {
    if (!draggingTree || !selectedTree) return;

    // ✅ block move if locked mid-drag
    if (selectedTree.userData?.locked) return;

    const rect = renderer.domElement.getBoundingClientRect();
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(mouse, camera);

    const hits = raycaster.intersectObjects(
      Array.isArray(targetMeshes) ? targetMeshes : [targetMeshes],
      true
    );
    if (!hits.length) return;

    const p = hits[0].point;
    selectedTree.position.x = p.x - dragOffset.x;
    selectedTree.position.y = p.y - dragOffset.y;

    selectedTree.position.z = 0;
    selectedTree.userData.groundZ = 0;

    highlight(selectedTree, true);
    if (selectedTree.userData.arrow) selectedTree.userData.arrow.visible = true;
  }

  function stopTreeDrag() {
    draggingTree = false;
    renderer.domElement.removeEventListener("pointermove", onTreeDragMove);

    if (selectedTree) {
      snapTreeToGround(selectedTree);
      highlight(selectedTree, true);

      const isLocked = !!selectedTree.userData?.locked;
      if (selectedTree.userData.arrow) selectedTree.userData.arrow.visible = !isLocked;
    }
  }

  renderer.domElement.addEventListener("pointermove", onHover);
  renderer.domElement.addEventListener("pointerdown", onPointerDown);
}
